/**
 * AI Purchase Analytics Slice
 * Manages the AI Purchase Numbers pages' overall/state/brand/store breakdowns.
 * The shared `fromDate`/`toDate` date range lives in the URL query string (see
 * `useAIPurchaseDateRangeParams`), not in this slice — each of the 3 pages owns its
 * own fetch-on-mount/fetch-on-param-change effect.
 * See dashboard-backend/docs/ai-purchase-tracking-plan.md for the backend contract.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { AIPurchaseAnalyticsState } from '../types';
import type {
    AIPurchaseByStateParams,
    AIPurchaseDateRangeParams,
    AIPurchaseOverallResponse,
    AIPurchaseStateBreakdownResponse,
    AIPurchaseBrandBreakdownResponse,
    AIPurchaseBrandListParams,
    AIPurchaseStoreBreakdownResponse,
    AIPurchaseStoreParams,
} from '../../types/api.types';
import apiClient from '../../utils/api/ApiClient';
import { API_ENDPOINTS, AI_PURCHASE_BRAND_DEFAULT_SORT } from '../../constants/api.constants';

const initialState: AIPurchaseAnalyticsState = {
    overall: null,
    states: [],
    brands: [],
    brandsForState: null,
    brandsPagination: null,
    stores: [],
    storesFor: null,
    loading: {
        overall: 'idle',
        states: 'idle',
        brands: 'idle',
        stores: 'idle',
    },
    error: {
        overall: null,
        states: null,
        brands: null,
        stores: null,
    },
};

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch overall AI purchase adoption summary (Level 1)
 */
export const fetchAIPurchaseOverall = createAsyncThunk(
    'aiPurchaseAnalytics/fetchOverall',
    async (params: AIPurchaseDateRangeParams) => {
        const response = await apiClient.post<AIPurchaseOverallResponse>(
            API_ENDPOINTS.AI_PURCHASE_ANALYTICS.OVERALL,
            params
        );

        return response.data;
    }
);

/**
 * Fetch per-state AI purchase adoption breakdown (Level 2). Called both unfiltered
 * (page 1, all 5 AI-enabled states) and filtered to one `state` (page 2's header, to get that
 * state's cumulative totals without a dedicated endpoint).
 */
export const fetchAIPurchaseByState = createAsyncThunk(
    'aiPurchaseAnalytics/fetchByState',
    async (params: AIPurchaseByStateParams) => {
        const response = await apiClient.post<AIPurchaseStateBreakdownResponse>(
            API_ENDPOINTS.AI_PURCHASE_ANALYTICS.BY_STATE,
            params
        );

        return response.data;
    }
);

/**
 * Fetch per-brand AI purchase adoption breakdown within a state (Level 3). Called on
 * both page 2 (to render the brand grid) and page 3 (to derive that one brand's
 * cumulative totals for the header, from the same list — no dedicated endpoint needed).
 * Supports server-side `search`/`skip`/`limit`/`sortValue` (see `AIPurchaseBrandListParams`).
 */
export const fetchAIPurchaseBrands = createAsyncThunk(
    'aiPurchaseAnalytics/fetchBrands',
    async ({ state, ...params }: AIPurchaseBrandListParams & { state: string }) => {
        const body: AIPurchaseBrandListParams = {
            ...params,
            sortValue: params.sortValue ?? AI_PURCHASE_BRAND_DEFAULT_SORT,
        };
        const response = await apiClient.post<AIPurchaseBrandBreakdownResponse>(
            API_ENDPOINTS.AI_PURCHASE_ANALYTICS.BY_STATE_BRANDS(state),
            body
        );

        return response.data;
    }
);

/**
 * Fetch per-store AI purchase adoption breakdown within a state, scoped to one brand
 * (Level 4, page 3's table). Always uses the body form of the endpoint
 * (`by-state/:state/stores` with `brandId`) since it is functionally identical to the
 * nested `by-state/:state/brands/:brandId/stores` alias per the plan doc (§5.2).
 * No `sortValue` — store sort is handled locally on the FE.
 */
export const fetchAIPurchaseStores = createAsyncThunk(
    'aiPurchaseAnalytics/fetchStores',
    async ({ state, ...params }: AIPurchaseStoreParams & { state: string }) => {
        const response = await apiClient.post<AIPurchaseStoreBreakdownResponse>(
            API_ENDPOINTS.AI_PURCHASE_ANALYTICS.BY_STATE_STORES(state),
            params
        );

        return response.data;
    }
);

// ============================================
// Slice
// ============================================

const aiPurchaseAnalyticsSlice = createSlice({
    name: 'aiPurchaseAnalytics',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Overall
            .addCase(fetchAIPurchaseOverall.pending, (state) => {
                state.loading.overall = 'loading';
                state.error.overall = null;
            })
            .addCase(fetchAIPurchaseOverall.fulfilled, (state, action) => {
                state.loading.overall = 'success';
                state.overall = action.payload.overall;
                state.error.overall = null;
            })
            .addCase(fetchAIPurchaseOverall.rejected, (state, action) => {
                state.loading.overall = 'error';
                state.error.overall =
                    action.error.message || 'Failed to fetch AI purchase overview';
            })

            // Per-state
            .addCase(fetchAIPurchaseByState.pending, (state) => {
                state.loading.states = 'loading';
                state.error.states = null;
            })
            .addCase(fetchAIPurchaseByState.fulfilled, (state, action) => {
                state.loading.states = 'success';
                state.states = action.payload.states;
                state.error.states = null;
            })
            .addCase(fetchAIPurchaseByState.rejected, (state, action) => {
                state.loading.states = 'error';
                state.error.states =
                    action.error.message || 'Failed to fetch AI purchase state breakdown';
            })

            // Per-brand
            .addCase(fetchAIPurchaseBrands.pending, (state) => {
                state.loading.brands = 'loading';
                state.error.brands = null;
            })
            .addCase(fetchAIPurchaseBrands.fulfilled, (state, action) => {
                state.loading.brands = 'success';
                state.brands = action.payload.brands;
                state.brandsForState = action.payload.state;
                state.brandsPagination = action.payload.pagination;
                state.error.brands = null;
            })
            .addCase(fetchAIPurchaseBrands.rejected, (state, action) => {
                state.loading.brands = 'error';
                state.error.brands =
                    action.error.message || 'Failed to fetch AI purchase brand breakdown';
            })

            // Per-store
            .addCase(fetchAIPurchaseStores.pending, (state) => {
                state.loading.stores = 'loading';
                state.error.stores = null;
            })
            .addCase(fetchAIPurchaseStores.fulfilled, (state, action) => {
                state.loading.stores = 'success';
                state.stores = action.payload.stores;
                state.storesFor = {
                    state: action.payload.state,
                    brandId: (action.meta.arg as { brandId?: string }).brandId ?? null,
                };
                state.error.stores = null;
            })
            .addCase(fetchAIPurchaseStores.rejected, (state, action) => {
                state.loading.stores = 'error';
                state.error.stores =
                    action.error.message || 'Failed to fetch AI purchase store breakdown';
            });
    },
});

// ============================================
// Selectors (Custom Hooks)
// ============================================

export const useAIPurchaseOverall = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.overall);
};

export const useAIPurchaseStates = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.states);
};

export const useAIPurchaseBrands = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.brands);
};

export const useAIPurchaseBrandsForState = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.brandsForState);
};

export const useAIPurchaseBrandsPagination = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.brandsPagination);
};

export const useAIPurchaseStores = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.stores);
};

export const useAIPurchaseStoresFor = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.storesFor);
};

export const useAIPurchaseLoading = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.loading);
};

export const useAIPurchaseError = () => {
    return useSelector((state: RootState) => state.aiPurchaseAnalytics.error);
};

// ============================================
// Reducer
// ============================================

export default aiPurchaseAnalyticsSlice.reducer;
